/**
 * AdukooAdSlot — v79.161 (Jul 11, 2026)
 *
 * Founder direction: "Utilise all spaces in Adukoo. We should not rely
 * on Google Ads. Admin uploads image/video ads via admin console; they
 * appear in feed / listings / profile / marketplace slots
 * automatically. Video keeps playing like Apple advertises on Pinterest."
 *
 * Drop `<AdukooAdSlot slot="funboard_feed" />` anywhere. Impressions
 * are fired-and-forgotten via a beacon. Clicks route through
 * /api/ads/click/{id} so the backend can count then redirect.
 * Non-dismissible per founder direction.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const IMPRESSION_CACHE = new Set();

export default function AdukooAdSlot({ slot = "any", className = "" }) {
    const { user } = useAuth();
    const [ad, setAd] = useState(null);
    const [muted, setMuted] = useState(true);
    const sentinelRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const country = (user?.country || "").toUpperCase() || undefined;
        const lang = (typeof navigator !== "undefined" ? navigator.language : "").split("-")[0].toLowerCase() || undefined;
        api.get("/ads/serve", { params: { slot, country, lang, limit: 1 } })
            .then((r) => {
                if (cancelled) return;
                const first = (r.data?.ads || [])[0];
                if (first) setAd(first);
            })
            .catch(() => { /* silent — ads are best-effort */ });
        return () => { cancelled = true; };
    }, [slot, user?.country]);

    useEffect(() => {
        if (!ad || !sentinelRef.current) return;
        const key = `${slot}:${ad.id}`;
        if (IMPRESSION_CACHE.has(key)) return;
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    IMPRESSION_CACHE.add(key);
                    try {
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/ads/impression/${encodeURIComponent(ad.id)}`;
                        if (navigator.sendBeacon) navigator.sendBeacon(url);
                        else fetch(url, { method: "POST", keepalive: true }).catch(() => {});
                    } catch { /* ignore */ }
                    io.disconnect();
                    break;
                }
            }
        }, { threshold: 0.5 });
        io.observe(sentinelRef.current);
        return () => io.disconnect();
    }, [ad, slot]);

    if (!ad) return null;

    const onClick = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.get(`/ads/click/${encodeURIComponent(ad.id)}`);
            const dest = data?.click_url || ad.click_url;
            if (dest) window.open(dest, "_blank", "noopener,noreferrer");
        } catch {
            if (ad.click_url) window.open(ad.click_url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div
            ref={sentinelRef}
            data-testid={`adukoo-ad-slot-${slot}-${ad.id}`}
            className={`relative rounded-2xl overflow-hidden bg-aduku-navy text-white shadow-md border border-white/10 ${className}`}
        >
            <a href={ad.click_url} onClick={onClick} className="block group">
                {ad.kind === "video" ? (
                    <div className="relative bg-black">
                        <video
                            src={ad.media_url}
                            className="w-full h-auto max-h-[70vh] object-contain"
                            autoPlay
                            muted={muted}
                            loop
                            playsInline
                            preload="metadata"
                        />
                        <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMuted((m) => !m); }}
                            className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white text-sm inline-flex items-center justify-center hover:bg-black/80"
                            aria-label={muted ? "Unmute" : "Mute"}
                        >
                            {muted ? "🔇" : "🔊"}
                        </button>
                    </div>
                ) : (
                    <img src={ad.media_url} alt={ad.headline || "Sponsored"} className="w-full h-auto max-h-[70vh] object-contain group-hover:brightness-110 transition" />
                )}
                <div className="p-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-aduku-yellow font-black">Sponsored · Adukoo</div>
                        {ad.headline && <div className="mt-0.5 font-display font-black text-white text-sm sm:text-base leading-snug">{ad.headline}</div>}
                    </div>
                    <span className="shrink-0 inline-flex items-center h-8 px-3 rounded-full bg-aduku-yellow text-aduku-navy text-xs font-black">
                        {ad.cta_label || "Learn more"} →
                    </span>
                </div>
            </a>
        </div>
    );
}
